import { reporter } from '~/hooks/useErrorReporter';
import {
  FlowAction,
  type FlowData___ConditionRepresentationFragment,
  type GetFlowsV2Query,
} from '~/graphql/types';
import { getCondition } from '../../../UpdateAction/components/Selector/utils/getConditions';
import getSubject from '../../../UpdateAction/components/Selector/utils/getSubject';
import getLabelForRep from '../../../UpdateAction/components/ConditionEditorV2/utils/getLabelForRep';
import type { IBuilderContext } from '../../../Builder/context';

const text = {
  notAvailable: 'Waarde is niet (meer) beschikbaar',
};

const getTriggerLabels = (
  flows: GetFlowsV2Query['getFlowsV2']['items'],
  memoizedIBuilderContext: Omit<
    IBuilderContext,
    'zapierTriggers' | 'offices' | 'users'
  >,
): Array<{ flowId: string; actionId: string; label: string }> => {
  try {
    const triggerLabels = flows
      .map(flow => {
        const startAction = flow.StartAction;

        if (!startAction || startAction.__typename !== 'FlowV2_Action_Start') {
          return null;
        }

        const condition = startAction.conditionList.conditions[0];

        if (!condition) {
          return {
            flowId: flow.id,
            actionId: startAction.id,
            label: text.notAvailable,
          };
        }

        const representations: Array<FlowData___ConditionRepresentationFragment> | null =
          condition.__typename === 'Flow___SubjectFieldCondition'
            ? getSubject(
                { type: condition.type, typeId: condition.typeId },
                memoizedIBuilderContext.opts.subjectMap,
              )?.fields.find(({ key }) => condition.field === key)
                ?.representation ?? null
            : getCondition(
                condition.type,
                memoizedIBuilderContext.opts.conditionMap,
              )?.representation ?? null;

        const labelStr =
          representations && representations.length > 0
            ? representations
                .map(rep =>
                  getLabelForRep(
                    {
                      representation: rep,
                      condition,
                      ctx: {
                        action: FlowAction.Start,
                        type: 'trigger',
                      },
                    },
                    {
                      conditionMap: memoizedIBuilderContext.opts.conditionMap,
                      directoryMap: memoizedIBuilderContext.opts.directoryMap,
                      instanceMap: memoizedIBuilderContext.instanceMap,
                      primitiveInputMap:
                        memoizedIBuilderContext.primitiveInputMap,
                      subjectMap: memoizedIBuilderContext.opts.subjectMap,
                    },
                  ),
                )
                .map(labelOutput =>
                  'result' in labelOutput
                    ? labelOutput.result
                    : labelOutput.error,
                )
                .join(' ')
            : null;

        return {
          flowId: flow.id,
          actionId: startAction.id,
          label: labelStr ?? text.notAvailable,
        };
      })
      .filter(
        (item): item is { flowId: string; actionId: string; label: string } =>
          item !== null,
      );

    return triggerLabels;
  } catch (error) {
    reporter.captureException(
      new Error(`Error in getTriggerLabels ${error}`),
      'warning',
    );
    return [];
  }
};

export default getTriggerLabels;
